import { TicketData } from '../types/printerProfile';
import { groupItemsByCategory } from './categoryGrouper';
import { formatOrderNumber } from './orderNumberFormatter';

export const buildLegacyTicket = (data: TicketData): string => {
  const {
    storeInfo,
    orderCode,
    items,
    categories = [],
    orderType,
    tableNumber,
    customerName,
    orderTotals,
    paymentMethod,
    taxSettings,
    taxResult,
    t,
    loyaltyDiscount = 0,
    loyaltyPointsUsed = 0,
    happyHourDiscount = 0,
    happyHourPercent = 0,
  } = data;

  // Format notes with proper line wrapping
  const formatItemNote = (note: string): string => {
    if (!note || note.trim() === '') return '';
    const prefix = '  Note: ';
    const maxWidth = 42;
    const availableWidth = maxWidth - prefix.length;
    const words = note.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      if ((currentLine + (currentLine ? ' ' : '') + word).length <= availableWidth) {
        currentLine += (currentLine ? ' ' : '') + word;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    }
    if (currentLine) lines.push(currentLine);

    return lines.map((line, idx) =>
      `[L]${idx === 0 ? prefix : '        '}${line}`
    ).join('\n');
  };

  // Format single item
  const formatItem = (item: any): string => {
    let result = `
[L]${item.quantity}x ${item.menuItem.name.padEnd(20, ' ')}[R]${t('Prix', {
      price: (item.menuItem.price * item.quantity).toFixed(2),
    })}
${Object.entries(item.customizations)
  .map(([_, options]) =>
    options
      .map(
        (opt) =>
          `[L]  - ${opt.name}${
            opt.price > 0
              ? ` (${t('Prix ajout', { price: opt.price.toFixed(2) })})`
              : ''
          }`
      )
      .join('\n')
  )
  .join('\n')}`;

    if (item.note && item.note.trim()) {
      result += '\n' + formatItemNote(item.note);
    }

    return result;
  };

  // Build items section with or without categories
  const showCategories = taxSettings?.showCategories && categories.length > 0;
  let itemsSection = '';

  if (showCategories) {
    const groupedItems = groupItemsByCategory(items, categories);
    itemsSection = groupedItems
      .map((group) => {
        let categorySection = `\n[C]*** ${group.categoryName.toUpperCase()} ***\n`;
        categorySection += group.items.map(formatItem).join('');
        return categorySection;
      })
      .join('\n[C]----------------\n');
  } else {
    itemsSection = items.map(formatItem).join('');
  }

  const ticketContent = `\x1B\x21\x18
[C]=== ** ${storeInfo?.name.toUpperCase() || t('Restaurant par défaut')} ** ===

\x1B\x21\x08[C]**** ${t('Commande numéro')} #${formatOrderNumber(orderCode)} ****

\x1B\x21\x00[L]${t('Date')}: ${new Date().toLocaleString('fr-FR')}
[L]${t('Type')}: **${orderType === 'takeaway' ? t('À emporter') : t('Sur place')}**${tableNumber ? ` - ${t('Table')} ${tableNumber}` : ''}
${customerName ? `[L]${t('Client')}: ${customerName}\n` : ''}
[C]----------------${itemsSection}
[C]----------------
[L]${t('Sous-total HT')}:         [R]${t('Prix', { price: (taxResult?.subtotalHT ?? orderTotals.subtotal).toFixed(2) })}
[L]${taxResult?.taxRate !== null && taxResult?.taxRate !== undefined ? t('TVA', { rate: (taxResult.taxRate % 1 === 0 ? taxResult.taxRate.toFixed(0) : taxResult.taxRate.toFixed(1)) }) : 'TVA'}: [R]${t('Prix', { price: (taxResult?.taxAmount ?? orderTotals.taxAmount).toFixed(2) })}
${happyHourDiscount > 0 ? `[L]${t('Happy Hour')} -${happyHourPercent}%: [R]-${t('Prix', { price: happyHourDiscount.toFixed(2) })}\n` : ''}
${loyaltyDiscount > 0 ? `[L]${t('Reduction fidelite')} (${loyaltyPointsUsed} pts): [R]-${t('Prix', { price: loyaltyDiscount.toFixed(2) })}\n` : ''}
\x1B\x21\x08[L]${t('Total TTC')}:     [R]${t('Prix', { price: Math.max(0, (taxResult?.totalTTC ?? orderTotals.invoiceTotal) - loyaltyDiscount - happyHourDiscount).toFixed(2) })}

\x1B\x21\x00[L]${t('Paiement')}:      [R]**${paymentMethod ? t(paymentMethod.charAt(0).toUpperCase() + paymentMethod.slice(1)) : t('Non spécifié')}**
[C]----------------
[C]${t('Merci de votre visite')}
`;

  return ticketContent;
};
